import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as OTPAuth from "otpauth";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const ISSUER = "CretikPanda Workspace";

function buildTotp(secret: string, label: string) {
  return new OTPAuth.TOTP({
    issuer: ISSUER,
    label,
    algorithm: "SHA1",
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(secret),
  });
}

// Begin enrollment: generates a fresh secret + otpauth URL.
// Does NOT enable MFA yet — confirmEnrollment must be called.
export const beginMfaEnrollment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: profile } = await supabase
      .from("profiles")
      .select("email, mfa_enabled")
      .eq("id", userId)
      .maybeSingle();
    if (profile?.mfa_enabled) {
      throw new Error("MFA is already enabled. Disable it first to re-enroll.");
    }
    const secret = new OTPAuth.Secret({ size: 20 }).base32;
    const totp = buildTotp(secret, profile?.email ?? "user");
    return { secret, otpauthUrl: totp.toString() };
  });

export const confirmMfaEnrollment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z.object({ secret: z.string().min(16), code: z.string().regex(/^\d{6}$/) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: profile } = await supabase
      .from("profiles")
      .select("email")
      .eq("id", userId)
      .maybeSingle();
    const totp = buildTotp(data.secret, profile?.email ?? "user");
    const delta = totp.validate({ token: data.code, window: 1 });
    if (delta === null) throw new Error("Invalid code. Try again.");
    // Ensure a private row exists, then store the secret there
    const { error: upsertErr } = await supabase
      .from("profiles_private")
      .upsert({ id: userId, mfa_secret: data.secret }, { onConflict: "id" });
    if (upsertErr) throw new Error(upsertErr.message);
    const { error } = await supabase
      .from("profiles")
      .update({ mfa_enabled: true })
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const disableMfa = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ code: z.string().min(6).max(6) }).parse(data))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: pub } = await supabase
      .from("profiles")
      .select("email, mfa_enabled")
      .eq("id", userId)
      .maybeSingle();
    const { data: priv } = await supabase
      .from("profiles_private")
      .select("mfa_secret, mfa_bypass_code")
      .eq("id", userId)
      .maybeSingle();
    if (!pub?.mfa_enabled || !priv?.mfa_secret) {
      throw new Error("MFA is not enabled.");
    }
    const totp = buildTotp(priv.mfa_secret, pub.email ?? "user");
    const valid =
      totp.validate({ token: data.code, window: 1 }) !== null ||
      data.code === priv.mfa_bypass_code;
    if (!valid) throw new Error("Invalid code.");
    const { error: privErr } = await supabase
      .from("profiles_private")
      .update({ mfa_secret: null })
      .eq("id", userId);
    if (privErr) throw new Error(privErr.message);
    const { error } = await supabase
      .from("profiles")
      .update({ mfa_enabled: false })
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Verify the post-login challenge. Accepts TOTP OR the user's bypass code.
export const verifyMfaChallenge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ code: z.string().min(6).max(6) }).parse(data))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: pub } = await supabase
      .from("profiles")
      .select("email, mfa_enabled")
      .eq("id", userId)
      .maybeSingle();
    if (!pub?.mfa_enabled) return { ok: true, usedBypass: false };

    const { data: priv } = await supabase
      .from("profiles_private")
      .select("mfa_secret, mfa_bypass_code")
      .eq("id", userId)
      .maybeSingle();

    const usedBypass = !!priv && data.code === priv.mfa_bypass_code;
    let valid = usedBypass;
    if (!valid && priv?.mfa_secret) {
      const totp = buildTotp(priv.mfa_secret, pub.email ?? "user");
      valid = totp.validate({ token: data.code, window: 1 }) !== null;
    }
    if (!valid) throw new Error("Invalid code.");

    if (usedBypass) {
      // Audit log via service role (table may have restrictive RLS)
      await supabaseAdmin.from("audit_log").insert({
        action_type: "mfa_bypass_used",
        admin_id: userId,
        target_user_id: userId,
        before_data: null,
        after_data: { at: new Date().toISOString() },
      });
    }
    return { ok: true, usedBypass };
  });

// Admin regenerates a user's bypass code
export const regenerateBypassCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ user_id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const isAdmin = (roles ?? []).some((r) => r.role === "admin");
    if (!isAdmin && userId !== data.user_id) {
      throw new Error("Not authorized");
    }
    // Find an unused 6-digit code
    let code = "";
    for (let i = 0; i < 50; i++) {
      const candidate = String(Math.floor(Math.random() * 1_000_000)).padStart(6, "0");
      const { data: hit } = await supabaseAdmin
        .from("profiles_private")
        .select("id")
        .eq("mfa_bypass_code", candidate)
        .maybeSingle();
      if (!hit) {
        code = candidate;
        break;
      }
    }
    if (!code) throw new Error("Could not generate a unique code");
    const { error } = await supabaseAdmin
      .from("profiles_private")
      .upsert({ id: data.user_id, mfa_bypass_code: code }, { onConflict: "id" });
    if (error) throw new Error(error.message);
    return { code };
  });
